import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, Inject, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { QuotesActions } from '../../../@store/quotes/quotes.actions';
import {
  selectArchivedQuoteError,
  selectArchivedQuoteSuccess,
  selectUnarchivedQuoteError,
  selectUnarchivedQuoteSuccess,
} from '../../../@store/quotes/quotes.selectors';
import { SafeAny } from '../../../types/safe-any';

@Component({
  selector: 'ciwc-change-quote-status-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  templateUrl: './change-quote-status-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeQuoteStatusDialogComponent {
  private readonly store = inject(Store);
  readonly archivedQuoteSuccess = this.store.selectSignal(selectArchivedQuoteSuccess);
  readonly archivedQuoteError = this.store.selectSignal(selectArchivedQuoteError);
  readonly unarchivedQuoteSuccess = this.store.selectSignal(selectUnarchivedQuoteSuccess);
  readonly unarchivedQuoteError = this.store.selectSignal(selectUnarchivedQuoteError);

  isDeleting = signal(false);

  constructor(
    public dialogRef: MatDialogRef<ChangeQuoteStatusDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { quoteId: SafeAny; archived: SafeAny },
  ) {
    effect(() => {
      if (this.isDeleting()) {
        const isArchived = this.data.archived;
        const successSignal = isArchived
          ? this.unarchivedQuoteSuccess()
          : this.archivedQuoteSuccess();
        const errorSignal = isArchived ? this.unarchivedQuoteError() : this.archivedQuoteError();

        if (successSignal) {
          this.dialogRef.close();
        }

        if (errorSignal) {
          this.isDeleting.set(false);
        }
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    this.isDeleting.set(true);

    if (this.data.archived) {
      this.store.dispatch(QuotesActions.unarchiveQuote({ id: this.data.quoteId, archived: false }));
    } else {
      this.store.dispatch(QuotesActions.archiveQuote({ id: this.data.quoteId, archived: true }));
    }
  }
}
